<template>
  <div>
    <div class="d-flex justify-content-between ts-mb-3">
      <h1 class="page-header ts-mb-0">{{ $t('khqrPayment.title') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          { text: $t('khqrPayment.title'), active: true }
        ]"
      />
    </div>

    <ts-card>
      <ts-card-wrapper>
        <div class="khqr-toolbar">
          <div></div>
          <div class="khqr-toolbar-right">
            <a-input-search
              v-model="search"
              class="khqr-search"
              size="small"
              :placeholder="$t('khqrPayment.search')"
              @search="fetchData"
            />
            <a-button icon="filter" @click="openFilterDrawer">
              {{ $t('khqrPayment.filter') }}
            </a-button>
          </div>
        </div>
      </ts-card-wrapper>

      <ts-loading-banner :loading="loading">
        <div class="khqr-table-shell">
          <Table
            :columns="columns"
            :data="resources"
            size="small"
            max-height="700"
            highlight-row
            stripe
          >
            <template slot-scope="{ row }" slot="branch">
              {{ row.branch_name_en || row.branch_name_kh || '-' }}
            </template>
            <template slot-scope="{ row }" slot="provider">
              <span class="khqr-pill khqr-pill-neutral">{{ providerLabel(row.provider) }}</span>
            </template>
            <template slot-scope="{ row }" slot="source_type">
              {{ sourceTypeLabel(row.source_type) }}
            </template>
            <template slot-scope="{ row }" slot="amount">
              <div class="khqr-amount">{{ formatAmount(row.amount, row.currency_symbol) }}</div>
            </template>
            <template slot-scope="{ row }" slot="bank">
              <div class="khqr-main-text">{{ row.bank_name || row.bank_name_kh || '-' }}</div>
              <div class="khqr-sub-text">{{ row.account_name || '-' }}</div>
            </template>
            <template slot-scope="{ row }" slot="status">
              <span class="khqr-pill" :class="statusClass(row.status)">{{ statusLabel(row.status) }}</span>
            </template>
            <template slot-scope="{ row }" slot="fulfillment_status">
              <span class="khqr-pill" :class="fulfillmentStatusClass(row.fulfillment_status)">
                {{ fulfillmentStatusLabel(row.fulfillment_status) }}
              </span>
            </template>
            <template slot-scope="{ row }" slot="event_count">
              <span class="khqr-event-count">{{ Number(row.event_count || 0) }}</span>
            </template>
            <template slot-scope="{ row }" slot="created_by">
              {{ row.created_by_name || row.created_by || '-' }}
            </template>
            <template slot-scope="{ row }" slot="created_at">
              {{ formatDateTime(row.created_at) }}
            </template>
            <template slot-scope="{ row }" slot="action">
              <div class="khqr-actions">
                <a
                  href="#"
                  v-tooltip="$t('khqrPayment.actions.viewDetail')"
                  class="khqr-action khqr-action-view"
                  @click.prevent="openDetail(row)"
                >
                  <i class="fas fa-eye"></i>
                </a>
                <a
                  v-if="canRetryRow(row)"
                  href="#"
                  v-tooltip="$t('khqrPayment.actions.retryFulfillment')"
                  class="khqr-action khqr-action-retry"
                  @click.prevent="retryFulfillment(row)"
                >
                  <i class="fas fa-redo"></i>
                </a>
              </div>
            </template>
            <template slot="empty">
              <div class="khqr-empty-state">
                <strong>{{ emptyStateTitle }}</strong>
                <span>{{ emptyStateHint }}</span>
              </div>
            </template>
          </Table>
        </div>
      </ts-loading-banner>

      <div class="khqr-pagination">
        <ts-total-list v-model="pagination" />
        <ts-pagination v-model="pagination" @navigate="fetchData" />
      </div>
    </ts-card>

    <a-drawer
      :visible="filterDrawerVisible"
      :title="$t('khqrPayment.filter')"
      width="380"
      placement="right"
      :zIndex="1021"
      @close="closeFilterDrawer"
    >
      <div class="khqr-filter-body">
        <div class="khqr-filter-field">
          <label>{{ $t('khqrPayment.table.branch') }}</label>
          <a-select
            v-model="filterForm.branch_ids"
            class="ts-w-full"
            mode="multiple"
            allow-clear
            :placeholder="$t('khqrPayment.allBranches')"
          >
            <a-select-option v-for="branch in options.branches" :key="branch.id" :value="branch.id">
              {{ branch.name || branch.name_kh }}
            </a-select-option>
          </a-select>
        </div>
        <div class="khqr-filter-field">
          <label>{{ $t('khqrPayment.table.bank') }}</label>
          <a-select v-model="filterForm.bank_id" class="ts-w-full" allow-clear :placeholder="$t('khqrPayment.allBanks')">
            <a-select-option v-for="bank in options.banks" :key="bank.id" :value="bank.id">
              {{ bank.name || bank.name_kh }}
            </a-select-option>
          </a-select>
        </div>
        <div class="khqr-filter-field">
          <label>{{ $t('khqrPayment.table.provider') }}</label>
          <a-select v-model="filterForm.provider" class="ts-w-full" allow-clear :placeholder="$t('khqrPayment.allProviders')">
            <a-select-option value="phillip">{{ $t('khqrPayment.providers.phillip') }}</a-select-option>
            <a-select-option value="aba">{{ $t('khqrPayment.providers.aba') }}</a-select-option>
          </a-select>
        </div>
        <div class="khqr-filter-field">
          <label>{{ $t('khqrPayment.table.status') }}</label>
          <a-select v-model="filterForm.status" class="ts-w-full" allow-clear :placeholder="$t('khqrPayment.allStatuses')">
            <a-select-option v-for="status in statuses" :key="status" :value="status">
              {{ statusLabel(status) }}
            </a-select-option>
          </a-select>
        </div>
        <div class="khqr-filter-field">
          <label>{{ $t('khqrPayment.table.fulfillmentStatus') }}</label>
          <a-select v-model="filterForm.fulfillment_status" class="ts-w-full" allow-clear :placeholder="$t('khqrPayment.allFulfillmentStatuses')">
            <a-select-option v-for="status in fulfillmentStatuses" :key="status" :value="status">
              {{ fulfillmentStatusLabel(status) }}
            </a-select-option>
          </a-select>
        </div>
        <div class="khqr-filter-field">
          <label>{{ $t('khqrPayment.table.createdAt') }}</label>
          <a-range-picker
            v-model="filterForm.created_at_range"
            class="ts-w-full"
            format="DD-MM-YYYY"
            valueFormat="DD-MM-YYYY"
          />
        </div>
      </div>
      <div class="khqr-filter-footer">
        <a-button @click="clearFilter">{{ $t('khqrPayment.clearFilter') }}</a-button>
        <a-button type="primary" @click="applyFilter">{{ $t('khqrPayment.applyFilter') }}</a-button>
      </div>
    </a-drawer>

    <khqr-attempt-detail-drawer
      :visible="detailVisible"
      :loading="detailLoading"
      :retrying="retryingId !== null"
      :attempt="selectedAttempt"
      :events="selectedEvents"
      :can-retry-fulfillment="canRetryFulfillment"
      @close="closeDetail"
      @retry="retryFulfillment"
    />
  </div>
</template>

<script>
import notificationAlert from '@/utils/NotificationAlert'
import { mapActions, mapState } from 'vuex'
import KhqrAttemptDetailDrawer from '../components/KhqrAttemptDetailDrawer.vue'

export default {
  name: 'KhqrPaymentIndex',
  components: { KhqrAttemptDetailDrawer },
  data() {
    return {
      loading: false,
      detailVisible: false,
      detailLoading: false,
      retryingId: null,
      selectedAttempt: null,
      selectedEvents: [],
      filterDrawerVisible: false,
      filters: this.emptyFilters(),
      filterForm: this.emptyFilters(),
      statuses: ['initiating', 'pending', 'paid', 'failed', 'cancelled'],
      fulfillmentStatuses: ['pending', 'processing', 'completed', 'failed', 'cancelled']
    }
  },
  computed: {
    ...mapState('khqrPayment', ['resources', 'pagination', 'options']),
    search: {
      get() {
        return this.$store.state.khqrPayment.search
      },
      set(value) {
        this.$store.commit('khqrPayment/SET_SEARCH', value)
        this.$store.commit('khqrPayment/RESET_CURRENT_PAGE')
      }
    },
    columns() {
      return [
        { title: this.$t('khqrPayment.table.branch'), slot: 'branch', minWidth: 150 },
        { title: this.$t('khqrPayment.table.provider'), slot: 'provider', width: 100, align: 'center' },
        { title: this.$t('khqrPayment.table.sourceType'), slot: 'source_type', width: 115 },
        { title: this.$t('khqrPayment.table.billNumber'), key: 'bill_number', minWidth: 170 },
        { title: this.$t('khqrPayment.table.amount'), slot: 'amount', align: 'right', width: 120 },
        { title: this.$t('khqrPayment.table.bank'), slot: 'bank', minWidth: 170 },
        { title: this.$t('khqrPayment.table.status'), slot: 'status', align: 'center', width: 105 },
        { title: this.$t('khqrPayment.table.fulfillmentStatus'), slot: 'fulfillment_status', align: 'center', width: 130 },
        { title: this.$t('khqrPayment.table.transactionId'), key: 'provider_transaction_id', minWidth: 165 },
        { title: this.$t('khqrPayment.table.eventCount'), slot: 'event_count', align: 'center', width: 80 },
        { title: this.$t('khqrPayment.table.createdBy'), slot: 'created_by', minWidth: 120 },
        { title: this.$t('khqrPayment.table.createdAt'), slot: 'created_at', width: 145 },
        { title: this.$t('khqrPayment.table.actions'), slot: 'action', fixed: 'right', align: 'center', width: 90 }
      ]
    },
    hasListCriteria() {
      return Boolean(
        this.$store.state.khqrPayment.search ||
        this.filters.branch_ids.length ||
        this.filters.bank_id ||
        this.filters.provider ||
        this.filters.status ||
        this.filters.fulfillment_status ||
        this.filters.created_at_range.length
      )
    },
    emptyStateTitle() {
      return this.hasListCriteria ? this.$t('khqrPayment.noFilteredRecords') : this.$t('khqrPayment.noRecords')
    },
    emptyStateHint() {
      return this.hasListCriteria ? this.$t('khqrPayment.noFilteredRecordsHint') : this.$t('khqrPayment.noRecordsHint')
    },
    canRetryFulfillment() {
      return this.can('view-khqr-payment')
    }
  },
  created() {
    this.fetchOptions()
    this.fetchConfig()
    this.fetchData()
  },
  methods: {
    ...mapActions('khqrPayment', ['fetch', 'fetchOptions', 'fetchConfig', 'find', 'getEvents', 'retryFulfillment']),
    can(permission) {
      return this.$store.getters.authPermissionAllow(permission)
    },
    emptyFilters() {
      return {
        branch_ids: [],
        bank_id: undefined,
        provider: undefined,
        status: undefined,
        fulfillment_status: undefined,
        created_at_range: []
      }
    },
    fetchData() {
      this.loading = true
      this.fetch(this.filters)
        .catch(notificationAlert)
        .finally(() => {
          this.loading = false
        })
    },
    openFilterDrawer() {
      this.filterForm = JSON.parse(JSON.stringify(this.filters))
      this.filterDrawerVisible = true
    },
    closeFilterDrawer() {
      this.filterDrawerVisible = false
    },
    applyFilter() {
      this.filters = JSON.parse(JSON.stringify(this.filterForm))
      this.$store.commit('khqrPayment/RESET_CURRENT_PAGE')
      this.closeFilterDrawer()
      this.fetchData()
    },
    clearFilter() {
      this.filterForm = this.emptyFilters()
      this.filters = this.emptyFilters()
      this.$store.commit('khqrPayment/RESET_CURRENT_PAGE')
      this.closeFilterDrawer()
      this.fetchData()
    },
    async openDetail(row) {
      this.detailVisible = true
      this.detailLoading = true
      this.selectedAttempt = row
      this.selectedEvents = []
      try {
        const [attemptResponse, eventResponse] = await Promise.all([
          this.find(row.id),
          this.getEvents(row.id)
        ])
        this.selectedAttempt = attemptResponse.data.data || row
        this.selectedEvents = eventResponse.data.data || []
      } catch (error) {
        notificationAlert(error)
      } finally {
        this.detailLoading = false
      }
    },
    closeDetail() {
      this.detailVisible = false
      this.selectedAttempt = null
      this.selectedEvents = []
    },
    async retryFulfillment(row) {
      if (!row || !this.canRetryRow(row)) return
      this.retryingId = row.id
      try {
        const response = await this.$store.dispatch('khqrPayment/retryFulfillment', row.id)
        this.$notification.success({
          message: this.$t('khqrPayment.success'),
          description: response.data.message || this.$t('khqrPayment.retrySuccess'),
          style: { zIndex: 1031 }
        })
        if (this.detailVisible) {
          await this.openDetail(row)
        } else {
          this.fetchData()
        }
      } catch (error) {
        notificationAlert(error)
      } finally {
        this.retryingId = null
      }
    },
    canRetryRow(row) {
      return row && row.status === 'paid' && row.fulfillment_status === 'failed' && this.canRetryFulfillment
    },
    formatAmount(value, symbol) {
      const amount = Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
      return `${symbol || '$'}${amount}`
    },
    formatDateTime(value) {
      if (!value) return '-'
      return String(value).replace('T', ' ').substring(0, 19)
    },
    providerLabel(value) {
      return this.translated(`khqrPayment.providers.${value}`, value)
    },
    sourceTypeLabel(value) {
      return this.translated(`khqrPayment.sourceTypes.${value}`, value)
    },
    statusLabel(value) {
      return this.translated(`khqrPayment.statuses.${value}`, value)
    },
    fulfillmentStatusLabel(value) {
      return this.translated(`khqrPayment.fulfillmentStatuses.${value || 'pending'}`, value || 'pending')
    },
    translated(key, fallback) {
      const text = this.$t(key)
      return text === key ? fallback || '-' : text
    },
    statusClass(status) {
      return {
        initiating: 'khqr-pill-muted',
        pending: 'khqr-pill-warning',
        paid: 'khqr-pill-success',
        failed: 'khqr-pill-danger',
        cancelled: 'khqr-pill-muted'
      }[status] || 'khqr-pill-muted'
    },
    fulfillmentStatusClass(status) {
      return {
        pending: 'khqr-pill-muted',
        processing: 'khqr-pill-info',
        completed: 'khqr-pill-success',
        failed: 'khqr-pill-danger',
        cancelled: 'khqr-pill-muted'
      }[status || 'pending'] || 'khqr-pill-muted'
    }
  }
}
</script>

<style scoped>
.khqr-toolbar,
.khqr-toolbar-right,
.khqr-actions,
.khqr-pagination {
  display: flex;
  align-items: center;
}
.khqr-toolbar {
  justify-content: space-between;
  gap: 12px;
}
.khqr-toolbar-right {
  gap: 8px;
  flex-wrap: wrap;
}
.khqr-search {
  width: 340px;
}
.khqr-table-shell {
  overflow-x: auto;
}
.khqr-main-text {
  font-weight: 600;
  color: #0f172a;
}
.khqr-sub-text {
  font-size: 12px;
  color: #64748b;
}
.khqr-amount {
  font-weight: 700;
  color: #0f172a;
}
.khqr-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 72px;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
.khqr-pill-neutral,
.khqr-pill-muted {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}
.khqr-pill-warning {
  background: #fffbeb;
  color: #b45309;
  border: 1px solid #fde68a;
}
.khqr-pill-success {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}
.khqr-pill-danger {
  background: #fff1f2;
  color: #be123c;
  border: 1px solid #fecdd3;
}
.khqr-pill-info {
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
}
.khqr-event-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  background: #eef2ff;
  color: #3730a3;
  font-weight: 700;
  font-size: 12px;
}
.khqr-actions {
  justify-content: center;
  gap: 6px;
}
.khqr-action {
  width: 25px;
  height: 25px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  text-decoration: none;
}
.khqr-action-view {
  background: #d1fae5;
  color: #047857;
}
.khqr-action-retry {
  background: #dbeafe;
  color: #1d4ed8;
}
.khqr-pagination {
  justify-content: space-between;
  padding: 15px;
}
.khqr-filter-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-bottom: 70px;
}
.khqr-filter-field label {
  display: block;
  margin-bottom: 5px;
  font-weight: 600;
  color: #334155;
}
.khqr-filter-footer {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #e2e8f0;
  background: #fff;
}
.khqr-empty-state {
  padding: 34px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: center;
  color: #64748b;
}
.khqr-empty-state strong {
  color: #0f172a;
}
@media (max-width: 768px) {
  .khqr-toolbar {
    align-items: stretch;
    flex-direction: column;
  }
  .khqr-toolbar-right,
  .khqr-search {
    width: 100%;
  }
}
</style>
